import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  Flame,
  Snowflake,
  TriangleAlert,
  Users as UsersIcon,
  UserMinus,
  XOctagon,
} from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { StatCard } from '@/components/app/StatCard';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { CopyableId } from '@/components/app/CopyableId';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { humanizeError } from '@/lib/apiClient';
import { shortId } from '@/lib/utils';
import { getAvatarUrl } from '@/lib/cdn';
import {
  useAdminRemoveMember,
  useFreezeTeam,
  useTeamDetail,
  useTeamLeaves,
  useTerminateTeam,
  useWarnTeam,
} from '../hooks';
import type { TeamMember, TeamStatus } from '../types';

type DialogKind = 'freeze' | 'warn' | 'terminate' | 'remove' | null;

function statusBadge(s: TeamStatus) {
  const map: Record<TeamStatus, 'success' | 'warning' | 'outline' | 'destructive'> = {
    active: 'success',
    warned: 'warning',
    frozen: 'outline',
    terminated: 'destructive',
  };
  return <Badge variant={map[s]}>{s}</Badge>;
}

export function TeamDetailPage() {
  const { teamId = '' } = useParams<{ teamId: string }>();
  const detail = useTeamDetail(teamId);
  const leaves = useTeamLeaves(teamId);
  const [dialog, setDialog] = React.useState<DialogKind>(null);
  const [removeTarget, setRemoveTarget] = React.useState<TeamMember | null>(null);

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <LoadingState label="Loading team" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorState
          title="Team failed to load"
          description={detail.error ? humanizeError(detail.error) : 'Not found.'}
          actionLabel="Retry"
          onAction={() => detail.refetch()}
        />
      </div>
    );
  }

  const t = detail.data.team;
  const members = detail.data.members;
  const isTerminated = t.status === 'terminated';

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        title={t.teamName}
        description={`${members.length} members · created ${t.createdAt.slice(0, 10)} · ${t.teamId}`}
        actions={
          <div className="flex items-center gap-2">
            {statusBadge(t.status)}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialog('warn')}
              disabled={isTerminated}
            >
              <TriangleAlert className="mr-1 h-4 w-4" /> Warn
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialog('freeze')}
              disabled={isTerminated || t.status === 'frozen'}
            >
              <Snowflake className="mr-1 h-4 w-4" /> Freeze
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDialog('terminate')}
              disabled={isTerminated}
            >
              <XOctagon className="mr-1 h-4 w-4" /> Terminate
            </Button>
          </div>
        }
      />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Streak" value={`${t.teamStreak ?? 0}d`} icon={Flame} />
        <StatCard label="Longest" value={`${t.longestTeamStreak ?? 0}d`} />
        <StatCard label="Check-ins" value={t.totalTeamCheckins ?? 0} icon={Building2} />
        <StatCard label="Level" value={t.teamLevel ?? '—'} />
      </section>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="leaves">Leaves</TabsTrigger>
          <TabsTrigger value="raw">Raw</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <UsersIcon className="h-4 w-4" /> Members
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {members.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No members.
                </p>
              ) : (
                <ul className="divide-y">
                  {members.map((m) => (
                    <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getAvatarUrl(m.avatarUrl)} alt="" />
                        <AvatarFallback>
                          {m.displayName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/users/${encodeURIComponent(m.userId)}`}
                            className="truncate text-sm font-medium hover:underline"
                          >
                            {m.displayName}
                          </Link>
                          <CopyableId value={m.userId} display={shortId(m.userId, 8, 6)} label="User ID" />
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {m.timeZone ?? '—'} ·
                          {m.userStreak != null ? ` ${m.userStreak}d streak ·` : ''}
                          {m.userPoints != null ? ` ${m.userPoints} XP ·` : ''}
                          {' '}
                          last check-in {m.lastCheckinAt ? m.lastCheckinAt.slice(0, 10) : '—'}
                        </div>
                      </div>
                      {m.isOnHoliday && <Badge variant="warning">Holiday</Badge>}
                      {!m.isActive && <Badge variant="outline">Inactive</Badge>}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setRemoveTarget(m);
                          setDialog('remove');
                        }}
                        disabled={isTerminated}
                      >
                        <UserMinus className="mr-1 h-3.5 w-3.5" /> Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Leave events</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {leaves.isLoading ? (
                <LoadingState />
              ) : leaves.isError ? (
                <ErrorState title="Leaves failed" description={humanizeError(leaves.error)} />
              ) : (leaves.data?.leaves?.length ?? 0) === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No leave events.
                </p>
              ) : (
                <ul className="divide-y text-sm">
                  {leaves.data?.leaves.map((l) => (
                    <li key={l.leaveId} className="flex items-center justify-between px-4 py-2">
                      <div>
                        <div className="font-medium">{l.reasonCode}</div>
                        <div className="text-xs text-muted-foreground">
                          {l.exitType} · {l.createdAt.slice(0, 10)} · initiated by {l.initiatorUserId.slice(0, 8)}…
                        </div>
                      </div>
                      <Badge variant="outline">{l.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="raw">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Raw JSON</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
                {JSON.stringify(detail.data, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {dialog === 'freeze' && (
        <FreezeDialog teamId={teamId} onClose={() => setDialog(null)} />
      )}
      {dialog === 'warn' && <WarnDialog teamId={teamId} onClose={() => setDialog(null)} />}
      {dialog === 'terminate' && (
        <TerminateDialog
          teamId={teamId}
          teamName={t.teamName}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'remove' && removeTarget && (
        <RemoveMemberDialog
          teamId={teamId}
          member={removeTarget}
          onClose={() => {
            setDialog(null);
            setRemoveTarget(null);
          }}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/teams"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="h-4 w-4" />
      Back to teams
    </Link>
  );
}

function FreezeDialog({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const mutation = useFreezeTeam(teamId);
  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Freeze team?"
      description="Freezing pauses team streak, check-ins, and chat. Reversible."
      reasonRequired={false}
      confirmLabel="Freeze"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({ reason: reason || undefined });
        onClose();
      }}
    />
  );
}

function WarnDialog({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const mutation = useWarnTeam(teamId);
  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Warn team?"
      description="Sends a system message to members and flags the team for follow-up."
      reasonRequired={false}
      confirmLabel="Warn"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({ reason: reason || undefined });
        onClose();
      }}
    />
  );
}

function TerminateDialog({
  teamId,
  teamName,
  onClose,
}: {
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const mutation = useTerminateTeam(teamId);
  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Terminate team?"
      description="Members are removed, streak resets, and the team is marked terminated. Not easily reversible."
      destructive
      typedConfirmation={teamName}
      confirmLabel="Terminate"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({ reason });
        onClose();
      }}
    />
  );
}

function RemoveMemberDialog({
  teamId,
  member,
  onClose,
}: {
  teamId: string;
  member: TeamMember;
  onClose: () => void;
}) {
  const mutation = useAdminRemoveMember(teamId);
  const [penalty, setPenalty] = React.useState('0');
  const [applyPrereq, setApplyPrereq] = React.useState(true);
  const parsedPenalty = Math.max(0, Number.parseInt(penalty, 10) || 0);

  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Remove ${member.displayName}?`}
      description="Removes the member from the team. Optionally applies a prerequisite and penalty."
      destructive
      body={
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="remove-penalty">Penalty points (0–10 000)</Label>
            <Input
              id="remove-penalty"
              type="number"
              inputMode="numeric"
              value={penalty}
              onChange={(e) => setPenalty(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={applyPrereq}
              onChange={(e) => setApplyPrereq(e.target.checked)}
            />
            <span>Apply prerequisite (user must complete onboarding before re-matching)</span>
          </label>
        </div>
      }
      confirmLabel="Remove member"
      submitting={mutation.isPending}
      onConfirm={async ({ reason }) => {
        await mutation.mutateAsync({
          targetUserId: member.userId,
          reason,
          applyPrerequisite: applyPrereq,
          penaltyPoints: parsedPenalty,
        });
        onClose();
      }}
    />
  );
}
